import { useState } from "react";
import { Bell, Timer } from "lucide-react";
import GlowCard from "../components/GlowCard";

/**
 * Rule Configuration — review SLAs and the notifications that chase them.
 *
 * NOTHING HERE IS WIRED, and the page says so. It is the prototype's layout rebuilt in the live
 * shell so the shape can be judged; the toggles move and the values are fixed.
 *
 * WHERE THIS IS GOING. These are not IQ Verity notifications in the end — they are SMRT tasks. A
 * finding that breaches an SLA, a critical finding needing a discipline lead, an escalation: each is
 * an assignment with an owner and a due date, which is the thing SMRT already models. Building a
 * second notification system beside it is the mistake this note exists to prevent.
 */

interface SlaTarget {
  severity: string;
  target: string;
}

const SLA: SlaTarget[] = [
  { severity: "Critical", target: "8 hours" },
  { severity: "Major", target: "24 hours" },
  { severity: "Minor", target: "48 hours" },
  { severity: "Observation", target: "5 working days" },
];

interface NotificationRule {
  key: string;
  title: string;
  detail: string;
  on: boolean;
}

const RULES: NotificationRule[] = [
  {
    key: "sla-at-risk",
    title: "SLA at risk",
    detail: "Alert the assigned reviewer 4 hours before breach",
    on: true,
  },
  {
    key: "critical-raised",
    title: "Critical finding raised",
    detail: "Immediate email to discipline lead",
    on: true,
  },
  {
    key: "escalation",
    title: "Escalation to senior authority",
    detail: "Notify engineering manager",
    on: true,
  },
  {
    key: "daily-digest",
    title: "Daily digest",
    detail: "Open findings summary at 07:00 local",
    on: false,
  },
  {
    key: "teams",
    title: "Microsoft Teams channel",
    detail: "Post run completions to #iqverity-reviews",
    on: true,
  },
];

const RuleConfiguration = () => {
  /**
   * The toggles move and nothing is saved.
   *
   * A switch that cannot be flipped tells a reviewer nothing about whether the control is the right
   * one. State resets on navigation, which is the honest behaviour for a control with no store
   * behind it yet.
   */
  const [on, setOn] = useState<Record<string, boolean>>(
    () => Object.fromEntries(RULES.map((r) => [r.key, r.on])),
  );
  /** The "what this covers" note. Closed by default — it is an explanation, not a heading. */
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <div className="vr-page">
      <div className="vr-page-head">
        <div>
          {/* Worth reading once, then in the way. Same "i" toggle the other headings use. */}
          <h1 className="vr-h-page">
            Rule Configuration
            <button
              type="button"
              className="vr-info-btn"
              aria-expanded={helpOpen}
              title={helpOpen ? "Hide" : "What this covers"}
              onClick={() => setHelpOpen((open) => !open)}
            >
              i
            </button>
          </h1>
          <p hidden={!helpOpen}>
            How quickly a finding must be answered, and who gets told when it is not. Escalation will
            run through SMRT tasks rather than a notification system of its own.
          </p>
        </div>
        <div className="vr-head-actions">
          <span className="vr-chip">Sample content</span>
        </div>
      </div>

      <div className="vr-grid vr-grid-2">
        <GlowCard hover={false}>
          <div className="vr-card-head">
            <Timer size={14} />
            <h3>Review SLA Targets</h3>
          </div>
          <p className="vr-muted vr-gov-lead">
            Measured from the moment a finding is raised to the moment a reviewer accepts or
            dismisses it.
          </p>
          <div className="vr-sla-list">
            {SLA.map((row) => (
              <div className="vr-sla-row" key={row.severity}>
                <span className="vr-sla-sev">{row.severity}</span>
                <span className="vr-sla-target">{row.target}</span>
              </div>
            ))}
          </div>
        </GlowCard>

        <GlowCard hover={false}>
          <div className="vr-card-head">
            <Bell size={14} />
            <h3>Notification Rules</h3>
          </div>
          <div className="vr-notif-list">
            {RULES.map((rule) => (
              <label className="vr-notif-row" key={rule.key}>
                <span className="vr-notif-body">
                  <span className="vr-notif-title">{rule.title}</span>
                  <span className="vr-notif-detail">{rule.detail}</span>
                </span>
                <input
                  type="checkbox"
                  className="vr-switch"
                  checked={on[rule.key] ?? false}
                  onChange={() => setOn((prev) => ({ ...prev, [rule.key]: !prev[rule.key] }))}
                />
              </label>
            ))}
          </div>
        </GlowCard>
      </div>
    </div>
  );
};

export default RuleConfiguration;
